import { GeneralLayout } from "@components";
import ErrorComponent from "@src/components/errors";
import FeatureBlock from "@src/modules/member/components/feature-block";
import { FeatureSlug } from "@src/modules/member/models/organization";
import { lazy } from "react";
import { Navigate } from "react-router-dom";

import OrganizationContextProvider from "../../../context/organization-context";
import { memberPathPrefix } from "../../../path";
import { memberExtraMenuList, memberMainMenuList } from "./menu";
import {
  CLIENT_DETAILS_PATH,
  CLIENT_LIST_PATH,
  DASHBOARD_PATH,
  FORMS_DETAILS_PATH,
  FORMS_LIST_PATH,
  FORM_NEW_FORM,
  INTERAC_LIST_PATH,
  INVOICE_DETAILS_PATH,
  INVOICE_LIST_PATH,
  PROJECT_DETAILS_PATH,
  PROJECT_LIST_PATH,
  RECURRING_INVOICE_DETAILS_PATH,
  RECURRING_INVOICE_LIST_PATH,
  RECURRING_INVOICE_NEW_FORM,
  SURCHARGE_DETAILS_PATH,
  SURCHARGE_LIST_PATH,
  TAXES_DETAILS_PATH,
  TAXES_LIST_PATH,
  TIME_ENTRIES_LIST_PATH,
  TRANSACTIONS_LIST_PATH,
} from "./paths";

const ClientDetails = lazy(
  () => import("@src/modules/member/modules/main/clients/details"),
);
const ClientList = lazy(
  () => import("@src/modules/member/modules/main/clients/list"),
);
const FormList = lazy(() => import("@src/modules/member/modules/main/forms"));
const InvoiceDetails = lazy(
  () => import("@src/modules/member/modules/main/invoices/invoices/details"),
);
const InvoicesModule = lazy(
  () => import("@src/modules/member/modules/main/invoices/invoices"),
);
const TransactionsList = lazy(
  () => import("@src/modules/member/modules/main/transactions/list"),
);
const InteracList = lazy(
  () => import("@src/modules/member/modules/main/interac/list/index"),
);
const SurchargeList = lazy(
  () => import("@src/modules/member/modules/main/fees/surcharges/list"),
);
const SurchargeDetails = lazy(
  () => import("@src/modules/member/modules/main/fees/surcharges/details"),
);
const TaxList = lazy(
  () => import("@src/modules/member/modules/main/fees/taxes"),
);
const TaxDetails = lazy(
  () => import("@src/modules/member/modules/main/fees/taxes/details"),
);
const SwitchOrganizationDialog = lazy(
  () =>
    import(
      "@src/modules/member/modules/main/router/menu/switch-organization-dialog"
    ),
);
const NewRecurringInvoice = lazy(
  () => import("@src/modules/member/modules/main/invoices/recurring/new"),
);
const RecurringInvoiceDetails = lazy(
  () => import("@src/modules/member/modules/main/invoices/recurring/details"),
);
const NewForm = lazy(
  () => import("@src/modules/member/modules/main/forms/new"),
);
const UpdateForm = lazy(
  () => import("@src/modules/member/modules/main/forms/update"),
);
const Dashboard = lazy(
  () => import("@src/modules/member/modules/main/dashboard"),
);
const TimeEntriesList = lazy(
  () => import("@src/modules/member/modules/main/time-entries"),
);
const ProjectList = lazy(
  () => import("@src/modules/member/modules/main/projects/list"),
);
const ProjectDetails = lazy(
  () => import("@src/modules/member/modules/main/projects/details"),
);
const RecurringInvoicesList = lazy(
  () => import("@src/modules/member/modules/main/invoices/recurring"),
);

const memberPath = `${memberPathPrefix}/`;
const protectedMemberRoutes = [
  {
    path: "",
    element: (
      <OrganizationContextProvider>
        <SwitchOrganizationDialog />
        <GeneralLayout
          menuList={memberMainMenuList}
          extraMenu={memberExtraMenuList}
        />
      </OrganizationContextProvider>
    ),
    children: [
      {
        errorElement: <ErrorComponent />,
        children: [
          {
            path: "",
            element: <Navigate to="invoices" />,
          },
          {
            path: DASHBOARD_PATH(true, memberPath),
            element: <Dashboard />,
          },
          {
            path: INVOICE_LIST_PATH(true, memberPath),
            element: <InvoicesModule />,
          },
          {
            path: INVOICE_DETAILS_PATH(true, memberPath),
            element: <InvoiceDetails />,
          },
          {
            path: RECURRING_INVOICE_LIST_PATH(true, memberPath),
            element: (
              <FeatureBlock feature={FeatureSlug.RecurringInvoices}>
                <RecurringInvoicesList />
              </FeatureBlock>
            ),
          },
          {
            path: RECURRING_INVOICE_NEW_FORM(true),
            element: (
              <FeatureBlock feature={FeatureSlug.RecurringInvoices}>
                <NewRecurringInvoice />
              </FeatureBlock>
            ),
          },
          {
            path: RECURRING_INVOICE_DETAILS_PATH(true),
            element: (
              <FeatureBlock feature={FeatureSlug.RecurringInvoices}>
                <RecurringInvoiceDetails />
              </FeatureBlock>
            ),
          },
          {
            path: TRANSACTIONS_LIST_PATH(true, memberPath),
            element: <TransactionsList />,
          },
          {
            path: INTERAC_LIST_PATH(true, memberPath),
            element: <InteracList />,
          },
          {
            path: SURCHARGE_LIST_PATH(true, memberPath),
            element: <SurchargeList />,
          },
          {
            path: SURCHARGE_DETAILS_PATH(true, memberPath),
            element: <SurchargeDetails />,
          },
          {
            path: TAXES_LIST_PATH(true, memberPath),
            element: <TaxList />,
          },
          {
            path: TAXES_DETAILS_PATH(true, memberPath),
            element: <TaxDetails />,
          },
          {
            path: CLIENT_LIST_PATH(true, memberPath),
            element: <ClientList />,
          },
          {
            path: CLIENT_DETAILS_PATH(true, memberPath),
            element: <ClientDetails />,
          },
          {
            path: TIME_ENTRIES_LIST_PATH(true, memberPath),
            element: <TimeEntriesList />,
          },
          {
            path: PROJECT_LIST_PATH(true, memberPath),
            element: <ProjectList />,
          },
          {
            path: PROJECT_DETAILS_PATH(true, memberPath),
            element: <ProjectDetails />,
          },
          {
            path: FORMS_LIST_PATH(true, memberPath),
            element: (
              <FeatureBlock feature={FeatureSlug.Forms}>
                <FormList />
              </FeatureBlock>
            ),
          },
          {
            path: FORM_NEW_FORM(true, memberPath),
            element: (
              <FeatureBlock feature={FeatureSlug.Forms}>
                <NewForm />
              </FeatureBlock>
            ),
          },
          {
            path: FORMS_DETAILS_PATH(true, memberPath),
            element: (
              <FeatureBlock feature={FeatureSlug.Forms}>
                <UpdateForm />
              </FeatureBlock>
            ),
          },
        ],
      },
    ],
  },
];

export default protectedMemberRoutes;
